import { AlertCircle, Loader2, Search } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { subscriptionService } from "../../services/subscriptionService";
import { SubscriptionSettings } from "../../types/subscription";
import AdminModalShell from "./AdminModalShell";
import SubscriptionAccessEditor, { SubscriptionAccessType } from "./SubscriptionAccessEditor";

interface ProviderItem {
  id: number;
  title: string;
  visibility: string;
}

interface AddSubscriberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (userId: string, settings: SubscriptionSettings) => Promise<void>;
  providerPortfolios: ProviderItem[];
  providerBaskets: ProviderItem[];
  existingSubscriberIds: string[];
  contentLoading: boolean;
}

const AddSubscriberModal: React.FC<AddSubscriberModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  providerPortfolios,
  providerBaskets,
  existingSubscriberIds,
  contentLoading,
}) => {
  // User search state
  const [emailSearch, setEmailSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchedUser, setSearchedUser] = useState<{ id: string; email: string; name: string | null } | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Access settings state
  const [portfolioAccess, setPortfolioAccess] = useState<SubscriptionAccessType>("all");
  const [basketAccess, setBasketAccess] = useState<SubscriptionAccessType>("all");
  const [selectedPortfolioIds, setSelectedPortfolioIds] = useState<number[]>([]);
  const [selectedBasketIds, setSelectedBasketIds] = useState<number[]>([]);
  const [isExternal, setIsExternal] = useState(true);

  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setEmailSearch("");
      setSearchedUser(null);
      setSearchError(null);
      setPortfolioAccess("all");
      setBasketAccess("all");
      setSelectedPortfolioIds([]);
      setSelectedBasketIds([]);
      setIsExternal(true);
      setSubmitError(null);
    }
  }, [isOpen]);

  const handleSearchUser = useCallback(async () => {
    if (!emailSearch.trim()) {
      setSearchError("Please enter an email address");
      return;
    }

    setSearching(true);
    setSearchError(null);
    setSearchedUser(null);

    try {
      const user = await subscriptionService.searchUserByEmail(emailSearch.trim());
      if (user) {
        // Check if already subscribed
        if (existingSubscriberIds.includes(user.id)) {
          setSearchError("This user is already subscribed to this provider");
        } else {
          setSearchedUser(user);
        }
      } else {
        setSearchError("No user found with this email address");
      }
    } catch (err) {
      console.error("User search error:", err);
      setSearchError("Failed to search for user. Please try again.");
    } finally {
      setSearching(false);
    }
  }, [emailSearch, existingSubscriberIds]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearchUser();
    }
  };

  const togglePortfolio = (id: number) => {
    setSelectedPortfolioIds((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const toggleBasket = (id: number) => {
    setSelectedBasketIds((prev) =>
      prev.includes(id) ? prev.filter((b) => b !== id) : [...prev, id]
    );
  };

  const buildSubscriptionSettings = (): SubscriptionSettings => {
    let portfolios: number[] = [];
    let baskets: number[] = [];

    if (portfolioAccess === "all") {
      portfolios = [];
    } else if (portfolioAccess === "none") {
      portfolios = [-1];
    } else {
      portfolios = selectedPortfolioIds;
    }

    if (basketAccess === "all") {
      baskets = [];
    } else if (basketAccess === "none") {
      baskets = [-1];
    } else {
      baskets = selectedBasketIds;
    }

    return {
      portfolios,
      baskets,
      isSubscribedExternallyToProvider: isExternal,
    };
  };

  const isFormValid = (): boolean => {
    if (!searchedUser) return false;
    // If specific access is selected, at least one item must be selected
    if (portfolioAccess === "specific" && selectedPortfolioIds.length === 0) return false;
    if (basketAccess === "specific" && selectedBasketIds.length === 0) return false;
    return true;
  };

  const handleSubmit = async () => {
    if (!searchedUser || !isFormValid()) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      await onSubmit(searchedUser.id, buildSubscriptionSettings());
    } catch (err) {
      console.error("Submit error:", err);
      setSubmitError(err instanceof Error ? err.message : "Failed to add subscriber");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AdminModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Add Subscriber"
      closeDisabled={submitting}
      footer={
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-300 disabled:opacity-50 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!isFormValid() || submitting}
            className="flex items-center rounded-lg bg-rp-amber px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-yellow-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Add Subscriber
          </button>
        </div>
      }
    >
            {/* User Search */}
            <div className="mb-6">
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                User Email *
              </label>
              <div className="flex space-x-2">
                <div className="flex-1 relative">
                  <input
                    type="email"
                    value={emailSearch}
                    onChange={(e) => {
                      setEmailSearch(e.target.value);
                      setSearchError(null);
                      setSearchedUser(null);
                    }}
                    onKeyPress={handleKeyPress}
                    placeholder="Enter user email..."
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 transition-all focus:border-rp-amber focus:outline-none focus:ring-1 focus:ring-rp-amber dark:border-rp-separator dark:bg-rp-surface dark:text-white dark:placeholder-gray-500"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleSearchUser}
                  disabled={searching || !emailSearch.trim()}
                  className="flex items-center rounded-lg bg-rp-amber px-4 py-2 text-white transition-colors hover:bg-yellow-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {searching ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                </button>
              </div>
              {searchError && (
                <p className="mt-2 flex items-center text-sm text-red-600 dark:text-red-400">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {searchError}
                </p>
              )}
              {searchedUser && (
                <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-rp-separator dark:bg-rp-surface">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {searchedUser.email}
                  </p>
                  {searchedUser.name && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {searchedUser.name}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Portfolio Access */}
            <SubscriptionAccessEditor
              title="Portfolio Access"
              name="portfolioAccess"
              access={portfolioAccess}
              setAccess={setPortfolioAccess}
              items={providerPortfolios}
              selectedIds={selectedPortfolioIds}
              toggleItem={togglePortfolio}
              itemLabel="portfolios"
              contentLoading={contentLoading}
            />

            {/* Basket Access */}
            <SubscriptionAccessEditor
              title="Basket Access"
              name="basketAccess"
              access={basketAccess}
              setAccess={setBasketAccess}
              items={providerBaskets}
              selectedIds={selectedBasketIds}
              toggleItem={toggleBasket}
              itemLabel="baskets"
              contentLoading={contentLoading}
            />

            {/* Subscription Source */}
            <div className="mb-6">
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={isExternal}
                  onChange={(e) => setIsExternal(e.target.checked)}
                  className="mt-0.5 rounded border-gray-300 text-rp-amber focus:ring-rp-amber dark:border-rp-separator"
                />
                <span>
                  <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Mark as external subscription
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Use this for admin-provisioned or imported subscribers.
                  </span>
                </span>
              </label>
            </div>

            {submitError && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                {submitError}
              </div>
            )}
    </AdminModalShell>
  );
};

export default AddSubscriberModal;
